/**
 * Fully procedural WebAudio soundscape — zero audio assets, so nothing to
 * license or download: wind-bed ambience, surface footsteps, XP chime, UI tick.
 * The context starts muted-by-browser until the first user gesture (autoplay
 * policy), so construction wires resume handlers instead of playing eagerly.
 */

export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private stepTimer = 0;

  onMutedChanged: ((muted: boolean) => void) | null = null;

  constructor() {
    const resume = () => this.ensure();
    window.addEventListener('pointerdown', resume);
    window.addEventListener('keydown', resume);
  }

  private ensure(): AudioContext | null {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return null;
      }
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 0.9;
      this.master.connect(this.ctx.destination);
      this.startWind();
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  toggleMuted(): void {
    this.muted = !this.muted;
    if (this.ctx && this.master) {
      this.master.gain.setTargetAtTime(this.muted ? 0 : 0.9, this.ctx.currentTime, 0.05);
    }
    this.onMutedChanged?.(this.muted);
  }

  /** 4s looped brown-noise buffer through a low-pass — reads as distant wind/city air. */
  private startWind(): void {
    const ctx = this.ctx!;
    const seconds = 4;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02; // brown-ish
      data[i] = last * 3.5;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 380;
    const gain = ctx.createGain();
    gain.gain.value = 0.05;
    // Slow swell so the wind breathes instead of droning.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.02;
    lfo.connect(lfoGain).connect(gain.gain);
    src.connect(filter).connect(gain).connect(this.master!);
    src.start();
    lfo.start();
  }

  /** Call every frame; schedules footsteps while the player moves.
   * `hardSurface` switches the footfall timbre (pavement click vs grass thud). */
  tick(dt: number, moving: boolean, sprinting: boolean, hardSurface: boolean): void {
    if (!this.ctx || this.muted) return;
    if (!moving) {
      this.stepTimer = 0.1;
      return;
    }
    this.stepTimer -= dt;
    if (this.stepTimer <= 0) {
      this.playStep(hardSurface);
      this.stepTimer = sprinting ? 0.31 : 0.45;
    }
  }

  /** Short filtered noise burst ≈ a footfall. */
  private playStep(hard: boolean): void {
    const ctx = this.ctx!;
    const dur = hard ? 0.07 : 0.11;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) ** 2;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = hard ? 900 + Math.random() * 500 : 320 + Math.random() * 220;
    const gain = ctx.createGain();
    gain.gain.value = (hard ? 0.08 : 0.12) + Math.random() * 0.05;
    src.connect(filter).connect(gain).connect(this.master!);
    src.start();
  }

  /** Rising three-note arpeggio for a level-up. */
  levelUp(): void {
    const ctx = this.ensure();
    if (!ctx || this.muted) return;
    for (const [freq, at] of [
      [523.25, 0],
      [659.25, 0.12],
      [783.99, 0.24],
      [1046.5, 0.38],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      const t0 = ctx.currentTime + at;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.16, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.7);
      osc.connect(gain).connect(this.master!);
      osc.start(t0);
      osc.stop(t0 + 0.75);
    }
  }

  /** Two-note rising chime for XP / level events. */
  chime(): void {
    const ctx = this.ensure();
    if (!ctx || this.muted) return;
    for (const [freq, at] of [
      [659.25, 0],
      [987.77, 0.1],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      const t0 = ctx.currentTime + at;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
      osc.connect(gain).connect(this.master!);
      osc.start(t0);
      osc.stop(t0 + 0.65);
    }
  }

  /** Soft blip for UI interactions (map open, view switch). */
  uiTick(): void {
    const ctx = this.ensure();
    if (!ctx || this.muted) return;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = 880;
    const gain = ctx.createGain();
    const t0 = ctx.currentTime;
    gain.gain.setValueAtTime(0.08, t0);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.08);
    osc.connect(gain).connect(this.master!);
    osc.start(t0);
    osc.stop(t0 + 0.1);
  }
}
